// Prisgraf via TradingView Lightweight Charts (v5) med stöd för ritbara stödlinjer.
// Färger anpassas automatiskt till ljust/mörkt tema via useTheme.

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type MouseEventParams,
} from 'lightweight-charts';
import { fetchHistory, type HistoryRange } from '../../lib/history';
import { pb } from '../../lib/pocketbase';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import type { CandlePoint, IndicatorPoint, ChartLine } from '../../types/stock';

interface Props {
  ticker: string;
  range: HistoryRange;
}

const RANGE_LABEL: Record<HistoryRange, string> = {
  '1m': '1 månad',
  '3m': '3 månader',
  '6m': '6 månader',
  '1y': '1 år',
  '2y': '2 år',
};

const CHART_HEIGHT = 500;

// Färgscheman för light och dark mode
const COLOR_SCHEMES = {
  light: {
    background: '#ffffff',
    text: '#475569',
    grid: '#f1f5f9',
    border: '#e2e8f0',
    candleUp: '#10b981',
    candleDown: '#ef4444',
    sma20: '#6366f1',
    sma50: '#ec4899',
    volumeUp: 'rgba(16, 185, 129, 0.4)',
    volumeDown: 'rgba(239, 68, 68, 0.4)',
    lineDefault: '#f59e0b',
  },
  dark: {
    background: '#0f172a',
    text: '#94a3b8',
    grid: '#1e293b',
    border: '#334155',
    candleUp: '#34d399',
    candleDown: '#f87171',
    sma20: '#818cf8',
    sma50: '#f472b6',
    volumeUp: 'rgba(52, 211, 153, 0.4)',
    volumeDown: 'rgba(248, 113, 113, 0.4)',
    lineDefault: '#fbbf24',
  },
} as const;

export default function StockChart({ ticker, range }: Props) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const sma20SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const sma50SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [drawingMode, setDrawingMode] = useState(false);
  const [priceInputOpen, setPriceInputOpen] = useState(false);
  const [priceInputValue, setPriceInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [chartLines, setChartLines] = useState<ChartLine[]>([]);

  // Beräkna aktuella färger baserat på tema
  const COLORS = useMemo(
    () => (isDark ? COLOR_SCHEMES.dark : COLOR_SCHEMES.light),
    [isDark],
  );

  // Refs för state som behöver läsas i useEffect utan att trigga om-render
  const drawingModeRef = useRef(drawingMode);
  const chartLinesRef = useRef(chartLines);
  useEffect(() => {
    drawingModeRef.current = drawingMode;
  }, [drawingMode]);
  useEffect(() => {
    chartLinesRef.current = chartLines;
  }, [chartLines]);

  // Rensa och rita alla stödlinjer
  const redrawChartLines = useCallback(() => {
    const series = candleSeriesRef.current;
    if (!series) return;

    // Ta bort alla befintliga prislinjer
    const existing = series.priceLines();
    if (existing && existing.length > 0) {
      existing.forEach((line) => {
        series.removePriceLine(line);
      });
    }

    // Rita alla nuvarande stödlinjer
    for (const line of chartLinesRef.current) {
      series.createPriceLine({
        price: line.price,
        color: line.color || COLORS.lineDefault,
        lineWidth: 2,
        lineStyle: 2,
        axisLabelVisible: true,
        title: line.label || 'Stöd',
      });
    }
  }, [COLORS.lineDefault]);

  // Ladda befintliga stödlinjer från PocketBase
  // Vänta på att auth är klar (max 2 sekunder)
  const loadChartLines = useCallback(async () => {
    // Vänta tills user är satt (auth-contextet har laddat)
    for (let i = 0; i < 20; i++) {
      if (pb.authStore.isValid && pb.authStore.model) break;
      await new Promise((r) => setTimeout(r, 100));
    }
    if (!pb.authStore.isValid) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const authModel = pb.authStore.model as any;
    if (!authModel?.id) {
      console.warn('loadChartLines: authModel saknas');
      return;
    }

    try {
      // Hämta ALLA linjer för denna användare (listRule begränsar redan till user)
      // Vi filtrerar client-side istället för i queryn — säkrare mot filter-syntax-fel
      const allRecords = await pb.collection('chart_lines').getFullList<ChartLine>({
        sort: '-created',
      });
      const records = allRecords.filter((r) => r.ticker === ticker);
      console.log(`loadChartLines: hittade ${records.length} stödlinjer för ${ticker} (av ${allRecords.length} totalt)`);
      setChartLines(records);
    } catch (e: any) {
      // Logga utförlig felinfo för felsökning
      console.error('loadChartLines fel:', {
        message: e?.message,
        status: e?.status,
        url: e?.url,
        response: e?.response,
        originalError: e?.originalError,
      });
      const detail = e?.response?.message || e?.message || 'Okänt fel';
      setError(`Kunde inte ladda stödlinjer: ${detail}`);
    }
  }, [ticker]);

  // Spara ny stödlinje till PocketBase
  const saveChartLine = useCallback(
    async (price: number) => {
      if (!pb.authStore.isValid) return;

      setSaving(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const authModel = pb.authStore.model as any;
        if (!authModel?.id) {
          throw new Error('Inte inloggad');
        }
        const record = await pb.collection('chart_lines').create({
          user: authModel.id,
          ticker,
          price,
          color: COLORS.lineDefault,
        });
        const newLine = record as unknown as ChartLine;
        // Uppdatera både state och ref direkt för att undvika race condition
        setChartLines((prev) => {
          const next = [newLine, ...prev];
          chartLinesRef.current = next;
          return next;
        });
        setDrawingMode(false);
        setPriceInputOpen(false);
        setPriceInputValue('');
      } catch (e) {
        console.error('Kunde inte spara stödlinje:', e);
        setError('Kunde inte spara stödlinje');
      } finally {
        setSaving(false);
      }
    },
    [ticker, COLORS.lineDefault],
  );

  // Ta bort stödlinje från PocketBase
  const deleteChartLine = async (id: string) => {
    if (!pb.authStore.isValid) return;

    try {
      await pb.collection('chart_lines').delete(id);
      setChartLines((prev) => {
        const next = prev.filter((line) => line.id !== id);
        chartLinesRef.current = next;
        return next;
      });
    } catch (e) {
      console.error('Kunde inte ta bort stödlinje:', e);
      setError('Kunde inte ta bort stödlinje');
    }
  };

  // Klickhanterare för chart — läses via ref
  const handleChartClick = useCallback((param: MouseEventParams) => {
    if (!drawingModeRef.current || !param.point || !candleSeriesRef.current) return;

    const price = candleSeriesRef.current.coordinateToPrice(param.point.y);
    if (price !== null && typeof price === 'number') {
      setPriceInputValue(price.toFixed(2));
      setPriceInputOpen(true);
    }
  }, []);

  // Stabil ref till click-handlern
  const handleChartClickRef = useRef(handleChartClick);
  handleChartClickRef.current = handleChartClick;

  // Bekräfta pris — läser från inputRef (senaste DOM-värdet) med
  // fallback till state. Detta undviker state-timing-problem.
  const confirmPriceLine = useCallback(() => {
    // Läs senaste värdet från DOM (säkrare än state)
    const domValue = priceInputRef.current?.value;
    const value = domValue ?? priceInputValue;
    const price = parseFloat(value);
    if (isNaN(price) || price <= 0) return;
    void saveChartLine(price);
  }, [priceInputValue, saveChartLine]);

  // Avbryt prisinmatning
  const cancelPriceLine = useCallback(() => {
    setPriceInputOpen(false);
    setPriceInputValue('');
    setDrawingMode(false);
  }, []);

  // Ladda stödlinjer när ticker ändras ELLER när auth blir klar
  useEffect(() => {
    if (user) {
      void loadChartLines();
    }
  }, [user, loadChartLines]);

  // Rita stödlinjer när chartLines ELLER chartReady ändras
  useEffect(() => {
    if (chartReady) {
      redrawChartLines();
    }
  }, [chartLines, chartReady, redrawChartLines]);

  // Uppdatera färg och rita om linjer när tema ändras
  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.applyOptions({
      layout: {
        background: { color: COLORS.background },
        textColor: COLORS.text,
      },
      grid: {
        vertLines: { color: COLORS.grid },
        horzLines: { color: COLORS.grid },
      },
      rightPriceScale: { borderColor: COLORS.border },
      timeScale: { borderColor: COLORS.border },
    });

    if (candleSeriesRef.current) {
      candleSeriesRef.current.applyOptions({
        upColor: COLORS.candleUp,
        downColor: COLORS.candleDown,
        borderUpColor: COLORS.candleUp,
        borderDownColor: COLORS.candleDown,
        wickUpColor: COLORS.candleUp,
        wickDownColor: COLORS.candleDown,
      });
    }

    if (sma20SeriesRef.current) {
      sma20SeriesRef.current.applyOptions({ color: COLORS.sma20 });
    }
    if (sma50SeriesRef.current) {
      sma50SeriesRef.current.applyOptions({ color: COLORS.sma50 });
    }

    if (chartReady) {
      redrawChartLines();
    }
  }, [COLORS, chartReady, redrawChartLines]);

  // Huvud-effect: skapa chart och hämta data
  useEffect(() => {
    if (!chartContainerRef.current) return;

    setDataLoading(true);
    setError(null);

    // Rensa tidigare chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      sma20SeriesRef.current = null;
      sma50SeriesRef.current = null;
      volumeSeriesRef.current = null;
      setChartReady(false);
    }

    let cancelled = false;

    fetchHistory(ticker, range)
      .then((data) => {
        if (cancelled) return;

        if (!data.points || data.points.length === 0) {
          setError('Ingen data tillgänglig för detta intervall');
          setDataLoading(false);
          return;
        }

        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }

        const container = chartContainerRef.current!;
        const chart = createChart(container, {
          width: container.clientWidth,
          height: CHART_HEIGHT,
          layout: {
            background: { color: COLORS.background },
            textColor: COLORS.text,
            fontFamily: 'system-ui, -apple-system, sans-serif',
          },
          grid: {
            vertLines: { color: COLORS.grid },
            horzLines: { color: COLORS.grid },
          },
          crosshair: { mode: 1 },
          rightPriceScale: { borderColor: COLORS.border },
          timeScale: {
            borderColor: COLORS.border,
            timeVisible: false,
            secondsVisible: false,
          },
        });

        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
          upColor: COLORS.candleUp,
          downColor: COLORS.candleDown,
          borderUpColor: COLORS.candleUp,
          borderDownColor: COLORS.candleDown,
          wickUpColor: COLORS.candleUp,
          wickDownColor: COLORS.candleDown,
        });

        candleSeriesRef.current = candleSeries;

        candleSeries.setData(
          data.points.map((p: CandlePoint) => ({
            time: p.time as Time,
            open: p.open,
            high: p.high,
            low: p.low,
            close: p.close,
          })),
        );

        const volumeSeries = chart.addSeries(HistogramSeries, {
          priceFormat: { type: 'volume' },
          priceScaleId: 'volume',
          color: COLORS.volumeUp,
        });

        chart.priceScale('volume').applyOptions({
          scaleMargins: { top: 0.8, bottom: 0 },
        });

        volumeSeries.setData(
          data.points.map((p: CandlePoint) => ({
            time: p.time as Time,
            value: p.volume,
            color: p.close >= p.open ? COLORS.volumeUp : COLORS.volumeDown,
          })),
        );

        if (data.indicators?.sma_20?.length) {
          const sma20 = chart.addSeries(LineSeries, {
            color: COLORS.sma20,
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          sma20SeriesRef.current = sma20;
          sma20.setData(
            data.indicators.sma_20.map((p: IndicatorPoint) => ({
              time: p.time as Time,
              value: p.value,
            })),
          );
        }

        if (data.indicators?.sma_50?.length) {
          const sma50 = chart.addSeries(LineSeries, {
            color: COLORS.sma50,
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: false,
          });
          sma50SeriesRef.current = sma50;
          sma50.setData(
            data.indicators.sma_50.map((p: IndicatorPoint) => ({
              time: p.time as Time,
              value: p.value,
            })),
          );
        }

        chart.timeScale().fitContent();

        // Använd ref för click-handler
        chart.subscribeClick((param) => handleChartClickRef.current(param));

        setChartReady(true);
        // Vänta en microtask så att stödlinjer från PB hinner laddas
        queueMicrotask(() => {
          if (!cancelled) {
            redrawChartLines();
          }
        });

        // Responsiv resize
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === container && chartRef.current) {
              chartRef.current.applyOptions({ width: entry.contentRect.width });
            }
          }
        });
        resizeObserver.observe(container);

        setDataLoading(false);

        return () => {
          resizeObserver.disconnect();
          if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
            candleSeriesRef.current = null;
            sma20SeriesRef.current = null;
            sma50SeriesRef.current = null;
            volumeSeriesRef.current = null;
            setChartReady(false);
          }
        };
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? 'Kunde inte ladda chart-data');
        setDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, range, COLORS.background, COLORS.text, COLORS.grid, COLORS.border, COLORS.candleUp, COLORS.candleDown, COLORS.volumeUp, COLORS.volumeDown, COLORS.sma20, COLORS.sma50]);

  return (
    <div className="rounded border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      {/* Header */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {RANGE_LABEL[range]} — Prisgraf
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLORS.sma20 }}
              />
              SMA(20)
            </span>
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: COLORS.sma50 }}
              />
              SMA(50)
            </span>
          </div>

          {user && (
            <button
              type="button"
              onClick={() => setDrawingMode(!drawingMode)}
              className={`rounded px-2 py-1 text-xs font-medium transition ${
                drawingMode
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {drawingMode ? 'Avbryt' : '+ Linje'}
            </button>
          )}
        </div>
      </div>

      {/* Prisinput-modal */}
      {priceInputOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800">
            <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">
              Ange pris för stödlinje
            </h3>
            <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
              Klicka på charten ger ungefärligt pris. Ange exakt värde nedan.
            </p>
            <input
              ref={priceInputRef}
              type="number"
              step="0.01"
              value={priceInputValue}
              onChange={(e) => setPriceInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmPriceLine();
                if (e.key === 'Escape') cancelPriceLine();
              }}
              autoFocus
              className="mb-4 w-full rounded border border-slate-300 bg-white px-3 py-2 text-lg tabular-nums focus:border-indigo-500 focus:outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelPriceLine}
                disabled={saving}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={confirmPriceLine}
                disabled={saving}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Sparar...' : 'Skapa linje'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statusmeddelande */}
      {drawingMode && !priceInputOpen && (
        <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          Klicka på charten för att välja pris (du kan justera det i dialogen som öppnas)
        </div>
      )}

      {/* Felmeddelande */}
      {error && (
        <div className="mb-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Lista över stödlinjer */}
      <div className="mb-2 space-y-1">
        {chartLines.length === 0 && user && pb.authStore.isValid && (
          <div className="rounded bg-slate-50 px-3 py-1.5 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
            Inga linjer utritade
          </div>
        )}
        {chartLines.map((line) => (
          <div
            key={line.id}
            className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-xs dark:bg-slate-800/50"
          >
            <span className="text-slate-700 dark:text-slate-300">
              Stödlinje: <strong>{line.price.toFixed(2)}</strong>
            </span>
            <button
              type="button"
              onClick={() => deleteChartLine(line.id)}
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Chart-container */}
      <div
        className="relative"
        style={{ height: CHART_HEIGHT, backgroundColor: COLORS.background }}
      >
        <div
          ref={chartContainerRef}
          className="absolute inset-0"
          style={{ cursor: drawingMode ? 'crosshair' : 'default' }}
        />

        {dataLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80">
            <div className="text-center">
              <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Laddar chart...
              </p>
            </div>
          </div>
        )}

        {error && !dataLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-900/80">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
